import { useState, useEffect, useCallback, useRef } from 'react';
import { taskService } from '../services/taskService';
import { PartnerTask, TaskStatus, TaskPriority } from '../types/partner';
import { useAuth } from '../context/AuthContext';

export interface TaskFilters {
  status?: TaskStatus | 'all'
  priority?: TaskPriority | 'all'
  search?: string
  view?: 'all' | 'my'
}

export interface TaskGroup {
  label: string
  tasks: PartnerTask[]
}

function groupTasksByDate(tasks: PartnerTask[]): TaskGroup[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekAgo = new Date(today.getTime() - 7 * 86400000);

  const groups: Record<string, PartnerTask[]> = {
    Today: [],
    Yesterday: [],
    'This Week': [],
    Older: [],
  };

  for (const task of tasks) {
    const d = new Date(task.createdAt);
    if (d >= today) groups['Today'].push(task);
    else if (d >= yesterday) groups['Yesterday'].push(task);
    else if (d >= weekAgo) groups['This Week'].push(task);
    else groups['Older'].push(task);
  }

  return Object.entries(groups)
    .filter(([, t]) => t.length > 0)
    .map(([label, t]) => ({ label, tasks: t }));
}

function generateSeedTasks(userId: string): PartnerTask[] {
  const now = new Date();
  const day = 86400000;
  return [
    {
      id: crypto.randomUUID(),
      title: 'Review quarterly customer health reports',
      description: 'Compile and review health metrics for all managed customers before the quarterly business review.',
      status: 'inprogress',
      priority: 'high',
      category: 'Reports',
      starred: true,
      completed: false,
      tags: ['quarterly', 'review'],
      subtasks: [
        { id: crypto.randomUUID(), title: 'Pull device uptime data', completed: true },
        { id: crypto.randomUUID(), title: 'Compile ticket resolution stats', completed: false },
        { id: crypto.randomUUID(), title: 'Draft executive summary', completed: false },
      ],
      createdAt: new Date(now.getTime() - 2 * 3600000).toISOString(),
      updatedAt: new Date(now.getTime() - 2 * 3600000).toISOString(),
      createdBy: userId,
    },
    {
      id: crypto.randomUUID(),
      title: 'Onboard new customer: Meridian Corp',
      description: 'Set up tenant, provision storage containers, and schedule kick-off meeting.',
      status: 'new',
      priority: 'urgent',
      category: 'Onboarding',
      starred: false,
      completed: false,
      tags: ['onboarding'],
      subtasks: [],
      createdAt: new Date(now.getTime() - 5 * 3600000).toISOString(),
      updatedAt: new Date(now.getTime() - 5 * 3600000).toISOString(),
      createdBy: userId,
    },
    {
      id: crypto.randomUUID(),
      title: 'Update marketplace asset catalogue',
      description: 'Upload new wallpaper assets and verify icon packs are synced to all partner containers.',
      status: 'pending',
      priority: 'medium',
      category: 'Marketplace',
      starred: false,
      completed: false,
      tags: ['marketplace', 'assets'],
      subtasks: [],
      createdAt: new Date(now.getTime() - day - 3 * 3600000).toISOString(),
      updatedAt: new Date(now.getTime() - day - 3 * 3600000).toISOString(),
      createdBy: userId,
    },
    {
      id: crypto.randomUUID(),
      title: 'Schedule demo call with prospect',
      description: 'Arrange a 30-minute product walkthrough for the potential Enterprise customer.',
      status: 'new',
      priority: 'normal',
      category: 'Sales',
      starred: true,
      completed: false,
      tags: ['sales', 'demo'],
      subtasks: [],
      createdAt: new Date(now.getTime() - day - 8 * 3600000).toISOString(),
      updatedAt: new Date(now.getTime() - day - 8 * 3600000).toISOString(),
      createdBy: userId,
    },
    {
      id: crypto.randomUUID(),
      title: 'Resolve device enrollment issue for Acme Inc',
      description: 'Three devices failing Intune enrollment. Investigate MDM profile and re-push.',
      status: 'completed',
      priority: 'high',
      category: 'Support',
      starred: false,
      completed: true,
      tags: ['support', 'intune'],
      subtasks: [],
      createdAt: new Date(now.getTime() - 3 * day).toISOString(),
      updatedAt: new Date(now.getTime() - 2 * day).toISOString(),
      createdBy: userId,
    },
  ];
}

export function useTasks() {
  const { partner, currentUserId } = useAuth();
  const partnerId = partner?.partnerId;
  const [tasks, setTasks] = useState<PartnerTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TaskFilters>({ status: 'all', priority: 'all', view: 'all', search: '' });
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistTasks = useCallback((updated: PartnerTask[]) => {
    if (!partnerId) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      taskService.saveTasks(partnerId, updated);
    }, 800);
  }, [partnerId]);

  const fetchTasks = useCallback(async () => {
    if (!partnerId) return;
    setIsLoading(true);
    setError(null);
    try {
      let data = await taskService.getTasks(partnerId);
      if (data.length === 0) {
        data = generateSeedTasks(currentUserId ?? 'unknown');
        await taskService.saveTasks(partnerId, data);
      }
      setTasks(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setIsLoading(false);
    }
  }, [partnerId, currentUserId]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const mutate = useCallback((updater: (prev: PartnerTask[]) => PartnerTask[]) => {
    setTasks(prev => {
      const next = updater(prev);
      persistTasks(next);
      return next;
    });
  }, [persistTasks]);

  const addTask = useCallback((task: Omit<PartnerTask, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => {
    const now = new Date().toISOString();
    const newTask: PartnerTask = {
      ...task,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
      createdBy: currentUserId ?? 'unknown',
    };
    mutate(prev => [newTask, ...prev]);
    return newTask;
  }, [mutate, currentUserId]);

  const updateTask = useCallback((id: string, updates: Partial<PartnerTask>) => {
    mutate(prev => prev.map(t =>
      t.id === id ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t
    ));
  }, [mutate]);

  const deleteTask = useCallback((id: string) => {
    mutate(prev => prev.filter(t => t.id !== id));
  }, [mutate]);

  const toggleComplete = useCallback((id: string) => {
    mutate(prev => prev.map(t =>
      t.id === id
        ? { ...t, completed: !t.completed, status: (!t.completed ? 'completed' : 'inprogress') as TaskStatus, updatedAt: new Date().toISOString() }
        : t
    ));
  }, [mutate]);

  const toggleStar = useCallback((id: string) => {
    mutate(prev => prev.map(t =>
      t.id === id ? { ...t, starred: !t.starred, updatedAt: new Date().toISOString() } : t
    ));
  }, [mutate]);

  const filteredTasks = tasks.filter(t => {
    if (filters.status && filters.status !== 'all' && t.status !== filters.status) return false;
    if (filters.priority && filters.priority !== 'all' && t.priority !== filters.priority) return false;
    if (filters.view === 'my' && t.createdBy !== currentUserId && t.assigneeId !== currentUserId) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!t.title.toLowerCase().includes(q) && !t.description.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const groupedTasks = groupTasksByDate(filteredTasks);

  const counts = {
    total: tasks.length,
    new: tasks.filter(t => t.status === 'new').length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inprogress: tasks.filter(t => t.status === 'inprogress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
  };

  return {
    tasks,
    filteredTasks,
    groupedTasks,
    counts,
    isLoading,
    error,
    filters,
    setFilters,
    addTask,
    updateTask,
    deleteTask,
    toggleComplete,
    toggleStar,
    refresh: fetchTasks,
  };
}
